// festivalsContext.tsx
import { Festival, FestivalsOrObservancesData, Location, Months } from '@/globalTypes';
import { parseDate } from '@/lib/globalHooks';
import { festivalSlug } from '@/lib/globalUtils';
import { getAFestival, getAllFestivals, getUserLocation } from '@/services/app.services';
import { usePathname, useRouter } from 'next/navigation';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
interface Tithi {
    name: string;
    key: string;
    end_time: number;
    start_time: number;
}
interface Masa {
    amanta: string;
    purnima: string;
}
interface Ayana {
    name: string;
    key: string;
}
interface Year {
    name: string;
    key: string;
    year: number;
}
interface Muhurat {
    auspicious: any[];
    inauspicious: any[];
}
interface Ritu {
    name: string;
    key: string;
    sub_text: string;
}
interface Vaara {
    name: string;
    key: string;
    sub_text: string;
}
interface PanchangaDetails {
    tithi: Tithi[];
    paksha: string;
    masa: Masa;
    paksha_v1: { name: string; key: string };
    nakshatra: any[];
    ayana: Ayana;
    shubh_karyas: any[];
    sun: { rise: number; set: number };
    moon: { rise: number; set: number };
    years: {
        vikram: Year;
        saka: Year;
        kali: Year;
    };
    muhurat: Muhurat;
    ritu: Ritu;
    vaara: Vaara;
    karana: any[];
    yoga: any[];
    observances: any[];
}
interface SelectedFestival {
    date: number;
    data: FestivalsOrObservancesData;
    panchanga: PanchangaDetails;
}
interface FestivalContextType {
    selectedYear: string;
    setSelectedYear: (year: string) => void;
    selectedFestival: Festival;
    setSelectedFestival: (festival: Festival) => void;
    loader: boolean;
    months: Months;
    setMonths: (months: Months) => void;
    handleYearChange: (year: number) => void;
    setLoader: (value: boolean) => void;
    location: Location;
    updatedSelectedFestival: (selectedFestival: Festival) => void
    fetchAllFestivals: () => void
    fetchUserLocation: () => void
    /** Slug of a festival picked from the listing whose dated details are still loading. */
    pendingSlug: string | null
}
const inital_months_data = {
    'JANUARY': [],
    'FEBRUARY': [],
    'MARCH': [],
    'APRIL': [],
    'MAY': [],
    'JUNE': [],
    'JULY': [],
    'AUGUST': [],
    'SEPTEMBER': [],
    'OCTOBER': [],
    'NOVEMBER': [],
    'DECEMBER': []
}
const FestivalContext = createContext<FestivalContextType | undefined>(undefined);
export const FestivalProvider = ({ children }: { children: ReactNode }) => {
    const [loader, setLoader] = useState(true)
    const [pendingSlug, setPendingSlug] = useState<string | null>(null);
    const router = useRouter();
    const pathname = usePathname();
    const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
    const [location, setlocation] = useState<Location>({
        city: '',
        lat: 0,
        long: 0
    });
    const [selectedFestival, setSelectedFestival] = useState<Festival>({
        date: 0,
        data: {
            id: "",
            name: "",
            key: "",
            type: "",
            sub_type: "",
            tier: 0,
            aliases: [],
            prarthanas:[],
            description_1: "",
            description_2: "",
            ui_info: {
                bg_image: "",
                bg_image_v1: "",
                share_image: "",
                share_image_v1: ""
            },
            eclipse_info: null,
            upcoming_dates: [],
            observance_id: "",
            observance_name: "",
            tithi_key: ""
        },
        panchanga: {
            tithi: [
                { name: "", key: "", end_time: 0, start_time: 0 },
                { name: "", key: "", end_time: 0, start_time: 0 }
            ],
            paksha: "",
            masa: { amanta: "", purnima: "" },
            paksha_v1: { name: "", key: "" },
            nakshatra: [],
            ayana: { name: '', key: '' },
            shubh_karyas: [],
            sun: { rise: 0, set: 0 },
            moon: { rise: 0, set: 0 },
            years: {
                vikram: { name: '', key: '', year: 0 },
                saka: { name: '', key: '', year: 0 },
                kali: { name: '', key: '', year: 0 }
            },
            muhurat: { auspicious: [], inauspicious: [] },
            ritu: { name: '', key: '', sub_text: '' },
            vaara: { name: '', key: '', sub_text: '' },
            karana: [],
            yoga: [],
            observances: []
        }
    });
    const [months, setMonths] = useState<Months>(inital_months_data);
    useEffect(() => {
        if (location.lat && location.long) {
            if (pathname === '/festivals') {
                setMonths(inital_months_data)
                fetchAllFestivals();
            }
        } else {
            fetchUserLocation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear, location.lat, location.long])


    const fetchUserLocation = async () => {
        let location: Location = JSON.parse(localStorage.getItem('userLocation') || JSON.stringify(await getUserLocation()));
        localStorage.setItem("userLocation", JSON.stringify(location));
        setlocation(location);
        return location;
    }

    const fetchAllFestivals = async () => {
        setLoader(true);
        let tempMonths: Months = { ...months };
        const promises = Object.keys(months).map(async (month, index) => {
            return {
                [month]: await getAllFestivals(parseDate(`${(index + 1).toString().padStart(2, '0')}-01-${selectedYear}`), location)
            };
        });
        promises.forEach(promise => {
            promise.then(result => {
                tempMonths = { ...tempMonths, ...result };
                setMonths({ ...tempMonths }); // Update state
            });
        });
        // Signal completion once EVERY month has resolved, so consumers know the
        // full list is rendered and the layout is stable (e.g. FestivalHome's
        // scroll-to-current-month, which otherwise drifts mid-load).
        Promise.all(promises).finally(() => setLoader(false));
    }

    const handleYearChange = (year: number) => {
        setLoader(true); // Uncomment if you have a loader state
        localStorage.setItem('selectedYear', JSON.stringify(year));
        setSelectedYear(year.toString());
    };

    // A festival picked from the listing: its details page must show the date
    // that was clicked (e.g. Pradosha on 6 Apr 2024), not the next occurrence.
    // pendingSlug tells FestivalView that this dated lookup is in flight, so it
    // doesn't start its own slug lookup, whose undated result (next occurrence)
    // used to overwrite this one whenever it finished second.
    const updatedSelectedFestival = async (selectedfestival: Festival) => {
        const slug = festivalSlug(selectedfestival.data);
        setLoader(true);
        setPendingSlug(slug);
        router.push("/festivals/" + slug);
        try {
            const data: Festival[] = await getAFestival(selectedfestival.date, location);
            const match = data?.find((festival) => festival.data.name === selectedfestival.data.name);
            // The observance lookup by date can come back without this festival;
            // keep the clicked date with the listing's own data in that case.
            // The slug is pinned to the one navigated to, so the details page
            // recognises this as its festival.
            const picked = match ? { ...match, date: match.date || selectedfestival.date } : selectedfestival;
            setSelectedFestival({ ...picked, data: { ...picked.data, slug } });
        } catch (error) {
            console.error("Error fetching festival:", error);
            setSelectedFestival({ ...selectedfestival, data: { ...selectedfestival.data, slug } });
        } finally {
            setLoader(false);
            setPendingSlug(null);
        }
    }
    return (
        <FestivalContext.Provider value={{ selectedYear, setSelectedYear, selectedFestival, setSelectedFestival, loader, months, setMonths, handleYearChange, setLoader, location, updatedSelectedFestival, fetchAllFestivals, fetchUserLocation, pendingSlug }}>
            {children}
        </FestivalContext.Provider>
    );
};
export const useFestivalContext = () => {
    const context = useContext(FestivalContext);
    if (context === undefined) {
        throw new Error('useFestivalContext must be used within a FestivalProvider');
    }
    return context;
};
